import { StateService } from '@infra/state/state.service';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { type ActivatedRouteSnapshot, type Resolve } from '@angular/router';
import { type AssignmentDTO } from '@api/models';
import { AssignmentService } from '@api/services/assignment.service';
import { SignalRService } from '@infra/services/signal-r.service';
import { filter, type Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

@Injectable()
export class AssignmentResolver implements Resolve<boolean> {
  private readonly stateService = inject(StateService);
  private readonly assignmentService = inject(AssignmentService);
  private readonly signalRService = inject(SignalRService);
  private readonly destroyRef = inject(DestroyRef);

  private assignmentId: string | null = null;

  constructor() {
    this.stateService.assignmentRefreshEvent$
      .pipe(
        filter(x => x === this.assignmentId),
        switchMap(x => this.assignmentService.getAssignment(x)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(assignment => {
        this.setAssignment(assignment);
      });
  }

  resolve(route: ActivatedRouteSnapshot): Observable<boolean> | boolean {
    this.assignmentId = route.paramMap.get('assignmentId');

    if (!this.assignmentId) {
      return false;
    }

    const currentAssignment = this.stateService.getAssignment();

    if (currentAssignment && currentAssignment.id === this.assignmentId) {
      return true;
    } else {
      return this.assignmentService.getAssignment(this.assignmentId).pipe(
        map((assignment: AssignmentDTO) => {
          this.setAssignment(assignment);
          this.signalRService.startConnection(assignment.companyId, this.assignmentId).then();
          return true;
        })
      );
    }
  }

  private setAssignment(assignment: AssignmentDTO): void {
    assignment.locations = [
      ...assignment.warehouseLocations,
      ...assignment.originLocations,
      ...assignment.destinationLocations,
    ];
    this.stateService.setAssignment(assignment, true);
  }
}
